/* SIZESOFTX
 =========================================================================================
 * Descripción: Tabla paginada con paginación y filtros del lado del servidor
 * Historial de cambios:
 * ---------------------------------------------------------------------------------------
 *    Revisión   | Fecha      | Desarrollador             | Resumen del cambio
 * ---------------------------------------------------------------------------------------
 *      1        | 09/04/2026 | Lic. José Luis Aragón C.  | Creación basada en SizeSoftXTablePaginadoFilter
 *      2        | 08/10/2026 | LIC. José Luis Aragón C.                       | Cabecero primary.main (igual que
 *              |            |                                  | demás buscadores) + avance visual:
 *              |            |                                  | foco + scroll al renglón
 *              |            |                                  | seleccionado (patrón Ventas)
 * --------------------------------------------------------------------------------------- */

import TextField from "../ui/TextField";
import { Fragment, useEffect, useState, useMemo, forwardRef, useRef } from "react";
import { Typography } from "@mui/material";
import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Paper, Box, Grid, Button } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PropTypes from "prop-types";

const SizeSoftXTablePaginadoFilterServidor = forwardRef((props, ref) => {
  const {
    data = [],
    columns,
    disableStickyHeader,
    totalDeRegistros = 0,
    setRow,
    defaultRow,
    row,
    id,
    fontSize = "medium",
    rowSpacing = 2,
    rowsPerPageSet = 9,
    EspaciadorColumnas = 12,
    pageNow = 0,
    initialFilter = "", // Filtro inicial opcional (del contexto)
    // Callbacks para paginación servidor
    onPageChangeExt,
    onRowsPerPageChangeExt,
    // Callback para filtro (por descripción)
    onFilterExt,
    // Prop para manejar doble click en filas
    onRowDoubleClick,
  } = props;

  const pager = [9, 10, 15, 20, 50, 100];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageSet);

  // Sincronizar rowsPerPage cuando cambia rowsPerPageSet
  useEffect(() => {
    setRowsPerPage(rowsPerPageSet);
  }, [rowsPerPageSet]);

  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState(true);
  const [filters, setFilters] = useState({});
  const [filterText, setFilterText] = useState("");
  const [filterInitialized, setFilterInitialized] = useState(false);

  // Calcular índices consecutivos basados en pégina real del servidor (pageNow viene del contexto)
  const dataRowsWithIndex = useMemo(() => {
    const startIndex = pageNow * rowsPerPage;
    return data.map((row, idx) => ({
      ...row,
      __rowIndex: startIndex + idx + 1,
    }));
  }, [data, pageNow, rowsPerPage]);
  useEffect(() => {
    if (!filterInitialized && initialFilter !== undefined) {
      setFilterText(initialFilter);
      setFilterInitialized(true);
    }
  }, [initialFilter, filterInitialized]);

  // Sincronizar pégina cuando cambia desde el contexto
  useEffect(() => {
    if (pageNow >= 0 && pageNow !== page) {
      setPage(pageNow);
    }
  }, [pageNow]);

  useEffect(() => {
    setPage(0);
  }, [totalDeRegistros]);

  const handleChangePage = (e, newPage) => {
    if (newPage === page) return;
    setPage(newPage);
    if (onPageChangeExt) {
      onPageChangeExt(newPage + 1);
    }
  };

  const handleChangeRowsPerPage = (e) => {
    const currentStartIndex = page * rowsPerPage;
    const newRowsPerPage = parseInt(e.target.value);
    setRowsPerPage(newRowsPerPage);
    // Calcular en qué pégina quedas basado en el índice actual para mantener posición
    const newPage = Math.floor(currentStartIndex / newRowsPerPage);
    setPage(newPage);
    if (onRowsPerPageChangeExt) {
      onRowsPerPageChangeExt(newRowsPerPage, newPage + 1);
    }
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilterText(value);
  };

  const handleFilterApply = () => {
    setPage(0);
    if (onFilterExt) {
      onFilterExt(filterText);
    }
  };

  const handleFilterClear = () => {
    setFilterText("");
    setPage(0);
    if (onFilterExt) {
      onFilterExt("");
    }
  };

  const handleClickRow = (valueRow) => () => {
    if (!defaultRow) {
      setRow(valueRow);
      return;
    }
    if (!row) {
      setRow(valueRow);
      return;
    }
    if (valueRow[id] === row[id]) {
      setRow(defaultRow);
      return;
    }
    setRow(valueRow);
  };

  // Rev 2: contenedor propio para el avance visual (foco + scroll al
  // seleccionado). Se fusiona con el ref reenviado al TableContainer.
  const containerRef = useRef(null);
  const mergeContainerRef = (node) => {
    containerRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  // Rev 2: avance visual del renglón seleccionado (foco + scroll dentro
  // del contenedor, sin mover la página). Patrón SizeSoftXTableVentas.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !row) return;
    const selected = container.querySelector("tr.row");
    if (!selected) return;
    try {
      selected.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch {}
    try {
      if (document.activeElement && container.contains(document.activeElement)) {
        selected.focus({ preventScroll: true });
      }
    } catch {}
  }, [row, data]);

  const handleClickOrder = (field, param = false) => {
    const orderField = field === orderBy ? (param ? order : !order) : order;
    setOrderBy(field);
    setOrder(orderField);
  };

  const getMaxWidthForColumn = (column) => {
    // Si la columna tiene width definido, usarlo
    if (column.width) return column.width;
    // Si no, calcular autométicamente baséndose en los datos
    if (!Array.isArray(data)) return 100;
    const maxDataWidth = data.reduce((maxWidth, row) => {
      const cellValue = row[column.field] || "";
      return Math.max(
        maxWidth,
        cellValue.toString().length * EspaciadorColumnas
      );
    }, 0);
    return maxDataWidth > 100 ? maxDataWidth : 100;
  };

  return (
    <Fragment>
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        <Grid item xs={12}>
          <Box sx={{ mb: 1, display: "flex", gap: 1, alignItems: "center" }}>
            <TextField autoComplete="off"
              size="small"
              placeholder="Filtrar por descripción..."
              value={filterText}
              onChange={handleFilterChange}
              onKeyDown={(e) => e.key === "Enter" && handleFilterApply()}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleFilterApply}
            >
              Buscar
            </Button>
            <Button variant="outlined" size="small" onClick={handleFilterClear}>
              Limpiar
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TableContainer
            component={Paper}
            ref={mergeContainerRef}
            tabIndex={0}
            sx={{ outline: "none" }}
          >
            <Table stickyHeader={!disableStickyHeader}>
              <TableHead>
                <TableRow>
                  {columns.map((column, i) => (
                    <TableCell
                      key={"f-" + i}
                      style={{
                        minWidth: getMaxWidthForColumn(column),
                        padding: "2px 8px",
                      }}
                      sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
                    ></TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {columns.map((column, i) => (
                    <TableCell
                      key={i}
                      style={{
                        minWidth: getMaxWidthForColumn(column),
                        whiteSpace: "nowrap",
                        fontSize,
                        padding: "4px 8px",
                      }}
                      sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
                    >
                      {!column.hideSort && (
                        <Button
                          style={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textTransform: "none",
                            fontSize,
                          }}
                          variant="text"
                          color="inherit"
                          className={
                            orderBy === column.field ? "sort" : "no-sort"
                          }
                          onClick={() => handleClickOrder(column.field)}
                          startIcon={
                            orderBy === column.field ? (
                              order ? (
                                <ArrowUpwardIcon />
                              ) : (
                                <ArrowDownwardIcon />
                              )
                            ) : (
                              <ArrowUpwardIcon />
                            )
                          }
                        >
                          <Typography
                            variant="button"
                            sx={{ fontWeight: "600", color: "primary.contrastText" }}
                            style={{
                              fontSize,
                              margin: "none",
                              padding: "none",
                            }}
                          >
                            {column.title}
                          </Typography>
                        </Button>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dataRowsWithIndex.length > 0 ? (
                  dataRowsWithIndex.map((dataRow, i) => (
                    <TableRow
                      key={i}
                      id={`row-${i}`}
                      tabIndex={-1}
                      onClick={handleClickRow(dataRow)}
                      onDoubleClick={() => onRowDoubleClick?.(dataRow)}
                      style={{ height: 6 + rowSpacing, fontSize, cursor: onRowDoubleClick ? 'pointer' : 'default', outline: "none" }}
                      className={
                        !row
                          ? "no-row"
                          : dataRow[id] === row[id]
                          ? "row"
                          : "no-row"
                      }
                    >
                      {columns.map((column, j) => (
                        <TableCell
                          key={j}
                          align={column.align || "left"}
                          style={{
                            fontSize,
                            padding: "4px 8px",
                            position: "relative",
                          }}
                        >
                          {!column.render ? (
                            <Typography>{dataRow[column.field]}</Typography>
                          ) : (
                            column.render(dataRow)
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography>
                        No se encontraron registros para mostrar
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box display="flex" justifyContent="center">
              {totalDeRegistros > 0 && (
                <TablePagination
                  component="div"
                  count={totalDeRegistros}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={pager}
                  labelRowsPerPage="Registros por pégina"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count}`
                  }
                />
              )}
            </Box>
          </TableContainer>
        </Grid>
      </Grid>
    </Fragment>
  );
});

SizeSoftXTablePaginadoFilterServidor.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      title: PropTypes.string,
      render: PropTypes.func,
      hideSort: PropTypes.bool,
      width: PropTypes.number,
      align: PropTypes.string,
    })
  ),
  data: PropTypes.array,
  id: PropTypes.string.isRequired,
  fontSize: PropTypes.oneOfType([PropTypes.oneOf(["small", "medium", "large"]), PropTypes.string]),
  rowSpacing: PropTypes.number,
  totalDeRegistros: PropTypes.number,
  rowsPerPageSet: PropTypes.number,
  pageNow: PropTypes.number,
  initialFilter: PropTypes.string,
  onPageChangeExt: PropTypes.func,
  onRowsPerPageChangeExt: PropTypes.func,
  onFilterExt: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
};

export default SizeSoftXTablePaginadoFilterServidor;
